// Auth context. Wraps Firebase Auth and surfaces:
//   - the current signed-in user (the fields the app reads — see SessionUser)
//   - the parsed OnLogic custom claims (role + capabilities)
//   - sign-in / sign-out actions
//
// The sign-in flow uses Google OIDC restricted to the onlogic.com domain via
// the `hd` parameter on the GoogleAuthProvider, AND a hard email-domain check
// after the redirect returns (defence in depth — `hd` is a hint that a
// malicious user can theoretically bypass with a custom IdP).
//
// Start-up: a returning user is rendered straight from the session Firebase
// Auth persisted on this device, and Firebase Auth's own verdict replaces it
// when it arrives (lib/persistedSession.ts explains why, and why that is safe).
// `sessionVerified` says which of the two the current state came from.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  browserPopupRedirectResolver,
  getRedirectResult,
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { CAP, type Capability, type OnLogicClaims } from '@onlogic/shared';

import { auth, buildGoogleProvider } from './firebase';
import { markRedirectPending, shouldSignInWithRedirect, takeRedirectPending } from './authDomain';
import { clearLastKnown } from './lastKnown';
import {
  isAllowedEmail,
  parseClaims,
  persistedSessionKey,
  readPersistedSession,
  sameClaims,
  sameSessionUser,
  toSessionUser,
  type PersistedSession,
  type SessionUser,
} from './persistedSession';

export type { SessionUser } from './persistedSession';

interface AuthState {
  user: SessionUser | null;
  /**
   * False while the state above comes from the persisted session and Firebase
   * Auth has not yet confirmed it. UX-only, like everything else here: the
   * server re-verifies every request regardless.
   */
  sessionVerified: boolean;
  claims: OnLogicClaims | null;
  realClaims: OnLogicClaims | null;
  isEmulated: boolean;
  toggleEmulation: () => void;
  loading: boolean;
  /** Why a redirect sign-in failed on the way back, for the login page. */
  signInError: string | null;
  signIn: () => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

/** The persisted session, read once before the first render (null if unusable). */
function readInitialSession(): PersistedSession | null {
  try {
    return readPersistedSession(localStorage, persistedSessionKey(auth.config.apiKey, auth.name));
  } catch {
    // localStorage itself can be unavailable in some privacy modes.
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Lazy initialisers: the persisted session is read exactly once, on mount.
  const [initialSession] = useState(readInitialSession);
  const [user, setUser] = useState<SessionUser | null>(() => initialSession?.user ?? null);
  const [realClaims, setRealClaims] = useState<OnLogicClaims | null>(
    () => initialSession?.claims ?? null,
  );
  const [sessionVerified, setSessionVerified] = useState(false);
  const [isEmulated, setIsEmulated] = useState(() => localStorage.getItem('onlogic_emulation') === 'true');
  // With a usable persisted session there is nothing to wait for: render now.
  const [loading, setLoading] = useState(() => !initialSession);
  // Back from a redirect sign-in (iPhone home-screen app, lib/authDomain.ts)?
  // Read once on mount; while it finishes, the app keeps showing "loading" so
  // the login page doesn't flash its button before the user arrives.
  const [completingRedirect, setCompletingRedirect] = useState(() => {
    try {
      return takeRedirectPending(sessionStorage);
    } catch {
      return false;
    }
  });
  const [signInError, setSignInError] = useState<string | null>(null);
  // Whose claims `realClaims` currently holds, for the offline fallback below.
  const claimsOwner = useRef<string | null>(initialSession?.user.uid ?? null);

  useEffect(() => {
    // Keep object identity when nothing changed, so confirming the persisted
    // session (same user, same claims) does not re-run every effect keyed on
    // `user` or `claims` — no listener re-subscribes just because Auth caught up.
    const commitUser = (next: SessionUser | null) =>
      setUser((prev) => (sameSessionUser(prev, next) ? prev : next));
    const commitClaims = (owner: string | null, next: OnLogicClaims | null) => {
      claimsOwner.current = owner;
      setRealClaims((prev) => (sameClaims(prev, next) ? prev : next));
    };

    // The handler awaits; a newer token change can land while an older one is
    // still in flight (e.g. sign-out during a claims read). Only the latest
    // callback may write state.
    let latest = 0;

    // onIdTokenChanged fires on sign-in, sign-out, AND when claims are refreshed
    // (e.g. after a Cloud Function grants a new role). This is what we want.
    // Its first call is Firebase Auth's verdict on the persisted session.
    const unsub = onIdTokenChanged(auth, async (fbUser) => {
      const call = ++latest;
      if (!fbUser) {
        commitUser(null);
        commitClaims(null, null);
        setSessionVerified(true);
        setLoading(false);
        return;
      }

      // Defence-in-depth domain check. The `hd` parameter on the provider is
      // only a hint; we enforce here so that even a misconfigured tenant
      // cannot let a non-@onlogic.com user in.
      if (!isAllowedEmail(fbUser.email)) {
        await signOut(auth);
        // eslint-disable-next-line no-console
        console.warn('Sign-in rejected: email domain not allowed');
        if (call !== latest) return;
        commitUser(null);
        commitClaims(null, null);
        setSessionVerified(true);
        setLoading(false);
        return;
      }

      let parsed: OnLogicClaims | null;
      try {
        const tokenResult = await fbUser.getIdTokenResult();
        parsed = parseClaims(tokenResult.claims as Record<string, unknown>);
      } catch (err) {
        // Offline with an expired token: Firebase Auth keeps the stored user
        // (a network failure is not a sign-out) but cannot refresh the token,
        // so reading the claims throws. This used to leave the app on the
        // loading skeleton for good. Keep the claims we already show for this
        // same account; with none, fall back to the plain employee view. UX
        // only — every server call re-verifies once the network is back.
        // eslint-disable-next-line no-console
        console.warn('[auth] Could not refresh the ID token; keeping the last known claims', err);
        parsed = null;
      }
      if (call !== latest) return;

      commitUser(toSessionUser(fbUser));
      if (parsed) {
        commitClaims(fbUser.uid, parsed);
      } else if (claimsOwner.current !== fbUser.uid) {
        commitClaims(fbUser.uid, { role: 'employee', capabilities: [] });
      }
      setSessionVerified(true);
      setLoading(false);
    });

    return unsub;
  }, []);

  // Finish a redirect sign-in. Only runs after one was started on this tab, so
  // an ordinary launch never loads the redirect resolver (the gapi script and
  // the auth iframe — see lib/firebase.ts). On success onIdTokenChanged above
  // receives the user and applies the same domain check as for the popup.
  useEffect(() => {
    if (!completingRedirect) return;
    let cancelled = false;
    getRedirectResult(auth, browserPopupRedirectResolver)
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Sign-in error (redirect):', err);
        const code = (err as { code?: string } | undefined)?.code ?? '';
        const message = (err as Error | undefined)?.message ?? String(err);
        if (!cancelled) setSignInError(`Sign-in failed (${code}): ${message}`);
      })
      .finally(() => {
        if (!cancelled) setCompletingRedirect(false);
      });
    return () => {
      cancelled = true;
    };
  }, [completingRedirect]);

  const toggleEmulation = () => {
    setIsEmulated((prev) => {
      const next = !prev;
      localStorage.setItem('onlogic_emulation', String(next));
      return next;
    });
  };

  const claims = useMemo<OnLogicClaims | null>(() => {
    if (!realClaims) return null;
    const canEmulate = realClaims.capabilities.includes(CAP.systemAdmin);
    if (isEmulated && canEmulate) {
      return {
        role: 'employee',
        capabilities: [], // Strip all admin privileges
        costCenter: realClaims.costCenter,
        site: realClaims.site,
        managerUid: realClaims.managerUid,
      };
    }
    return realClaims;
  }, [realClaims, isEmulated]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      sessionVerified,
      claims,
      realClaims,
      isEmulated: isEmulated && (realClaims?.capabilities.includes(CAP.systemAdmin) ?? false),
      toggleEmulation,
      loading: loading || completingRedirect,
      signInError,
      signIn: async () => {
        setSignInError(null);
        // The resolver is supplied here, at the sign-in call sites, instead of
        // at `initializeAuth` (see lib/firebase.ts): wiring it in at boot
        // makes the SDK load Google's gapi script and the auth iframe on every
        // mobile start-up, before the persisted session is restored.
        const iosNavigator = navigator as Navigator & { standalone?: boolean };
        if (
          shouldSignInWithRedirect({
            standalone: iosNavigator.standalone,
            authDomain: auth.config.authDomain,
            host: location.host,
          })
        ) {
          // iPhone home-screen app: a popup opens in a sheet that cannot report
          // back, so leave the page for Google and finish on return (GitHub issue 296).
          markRedirectPending(sessionStorage);
          try {
            await signInWithRedirect(auth, buildGoogleProvider(), browserPopupRedirectResolver);
          } catch (err) {
            takeRedirectPending(sessionStorage);
            throw err;
          }
          return;
        }
        await signInWithPopup(auth, buildGoogleProvider(), browserPopupRedirectResolver);
      },
      signOutUser: async () => {
        // Drop this device's last-known settings with the session
        // (lib/lastKnown.ts) — nothing sensitive, but they belong to it.
        clearLastKnown();
        await signOut(auth);
      },
    }),
    [user, sessionVerified, claims, realClaims, isEmulated, loading, completingRedirect, signInError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience: does the current user have the given capability? */
export function useHasCapability(cap: Capability): boolean {
  const { claims } = useAuth();
  return claims?.capabilities?.includes(cap) ?? false;
}

/**
 * Does the current user have *any* of the listed capabilities? Use this
 * instead of `useHasCapability('a') || useHasCapability('b')` — the
 * short-circuiting OR conditionally skips the second hook call, which
 * violates the React rules of hooks.
 */
export function useHasAnyCapability(caps: readonly Capability[]): boolean {
  const { claims } = useAuth();
  const owned = claims?.capabilities ?? [];
  return caps.some((c) => owned.includes(c));
}
